import React, { useState } from 'react';
import {
	Laboratory,
	useCreateLaboratoryRequisitionWorkplaceMutation,
	useCreateLaboratoryWorkplaceMutation,
	useDeleteLaboratoryWorkplaceMutation,
	useLaboratoryRequestsQuery,
	useDeleteLaboratoryRequisitionWorkplaceMutation,
} from '../../../../graphql/graphql';
// @ts-ignore
import styles from './LaboratorySetupItem.module.scss';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import LaboratorySetupAllowConfirmationModal from './LaboratorySetupAllowConfirmationModal';
import useNotification from '../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../Hooks/useTranslation';
import Loading from '../../../../Components/Loading';
import Text from '../../../../Components/Base/Text/Text';

interface Props {
	item: Laboratory;
	allowed: boolean;
	workplaceId: string;
	onChange: () => void;
}

const LaboratorySetupItem: React.FC<Props> = ({
	item,
	workplaceId,
	allowed,
	onChange,
}) => {
	const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
	const [createLaboratoryWorkplace] = useCreateLaboratoryWorkplaceMutation();
	const [deleteLaboratoryWorkplace] = useDeleteLaboratoryWorkplaceMutation();
	const [createLaboratoryRequisitionWorkplace] =
		useCreateLaboratoryRequisitionWorkplaceMutation();
	const [deleteLaboratoryRequisitionWorkplace] =
		useDeleteLaboratoryRequisitionWorkplaceMutation();
	const {
		data,
		isLoading,
		refetch: reFetchRequests,
	} = useLaboratoryRequestsQuery({
		where: { laboratoryId: item.id },
	});
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();

	const handleConfirm = async () => {
		const result = await createLaboratoryWorkplace({
			input: { laboratoryId: item.id, workplaceId },
		});
		if ('data' in result) {
			showNotificationSuccess({
				content: t('component.laboratorySetup.laboratoryAllowed'),
			});
		}
		setIsConfirmationOpen(false);
		onChange();
	};

	const handleDisable = async () => {
		const result = await deleteLaboratoryWorkplace({
			input: {
				laboratoryId: item.id,
				workplaceId,
			},
		});
		if ('data' in result) {
			showNotificationSuccess({
				content: t('component.laboratorySetup.laboratoryDisabled'),
			});
		}
		onChange();
	};

	const handleEnableRequisition = async (id: string) => {
		const result = await createLaboratoryRequisitionWorkplace({
			input: {
				workplaceId,
				laboratoryRequisitionId: id,
			},
		});
		if ('data' in result) {
			showNotificationSuccess({
				content: t('component.laboratorySetup.laboratoryRequestAllowed'),
			});
		}
		reFetchRequests();
	};

	const handleDisableRequisition = async (id: string) => {
		const result = await deleteLaboratoryRequisitionWorkplace({
			input: {
				workplaceId,
				laboratoryRequisitionId: id,
			},
		});
		if ('data' in result) {
			showNotificationSuccess({
				content: t('component.laboratorySetup.laboratoryRequestDisabled'),
			});
		}
		reFetchRequests();
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<>
			<div className={styles.item}>
				<div className={styles.title}>{item.name}</div>
				{allowed ? (
					<Button
						theme={Theme.RED}
						title='component.laboratorySetup.disable'
						onClick={handleDisable}
					/>
				) : (
					<Button
						theme={Theme.BLUE}
						title='component.laboratorySetup.allow'
						onClick={() => setIsConfirmationOpen(true)}
					/>
				)}
			</div>
			{allowed && (
				<div className={styles.requisitions}>
					{data?.laboratoryRequisitions?.data?.map(requisition => {
						return (
							<div className={styles.item}>
								<div className={styles.title}>{requisition.name}</div>
								{requisition.allowed ? (
									<Button
										theme={Theme.RED}
										title='component.laboratorySetup.disable'
										onClick={() => handleDisableRequisition(requisition.id)}
									/>
								) : (
									<Button
										theme={Theme.BLUE}
										title='component.laboratorySetup.allow'
										onClick={() => handleEnableRequisition(requisition.id)}
									/>
								)}
							</div>
						);
					})}
				</div>
			)}
			{isConfirmationOpen && (
				<LaboratorySetupAllowConfirmationModal
					onConfirm={handleConfirm}
					onClose={() => setIsConfirmationOpen(false)}
					laboratoryName={item.name}
				/>
			)}
		</>
	);
};

export default LaboratorySetupItem;
